import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogModule,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { TablerIconsModule } from 'angular-tabler-icons';

export interface ManualReminderRecipient {
  name: string;
  email: string;
}

export interface ManualReminderConfirmationData {
  invoiceRef: string;
  corporateName?: string;
  reminderLevel: number;
  sourceLabel: string;
  recipients: ManualReminderRecipient[];
  confirmText?: string;
  cancelText?: string;
}

@Component({
  selector: 'app-manual-reminder-confirmation-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogActions,
    MatDialogClose,
    MatDialogTitle,
    MatDialogContent,
    MatDialogModule,
    TablerIconsModule
  ],
  template: `
    <div class="header">
      <h5 mat-dialog-title>Send manual reminder</h5>
    </div>

    <div mat-dialog-content class="content">
      <div class="lead">
        You are about to send reminder level <strong>{{ data.reminderLevel }}</strong>
        for invoice <strong>{{ data.invoiceRef }}</strong>.
      </div>

         <div class="source-card">
        <i-tabler name="info-circle" class="icon-16"></i-tabler>
        <span><strong>Recipients source:</strong> {{ data.sourceLabel }}</span>
      </div>
      <div class="corporate-info-card" *ngIf="data.corporateName">
        <div class="corporate-card-icon">
          <i-tabler name="building" class="icon-16"></i-tabler>
        </div>
        <div class="corporate-card-content">
          <span class="corporate-card-label">Corporate</span>
          <span class="corporate-card-value">{{ data.corporateName }}</span>
        </div>
      </div>

   

      <div class="recipients-block">
        <div class="recipients-title">
          <i-tabler name="users" class="icon-16"></i-tabler>
          <span>Recipients ({{ data.recipients.length }})</span>
        </div>

        <ul *ngIf="data.recipients.length > 0; else noRecipient" class="recipients-list">
          <li *ngFor="let recipient of data.recipients">
            <span class="recipient-name">{{ recipient.name }}</span>
            <span class="recipient-email">{{ recipient.email }}</span>
          </li>
        </ul>
        <ng-template #noRecipient>
          <div class="no-recipient">
            No billing contact email found for this invoice.
          </div>
        </ng-template>
      </div>
    </div>

    <div mat-dialog-actions class="actions">
      <button
        mat-flat-button
        class="cancel-btn"
        [mat-dialog-close]="false">
        {{ data.cancelText || 'Cancel' }}
      </button>
      <button
        mat-flat-button
        class="confirm-btn"
        [mat-dialog-close]="true"
        cdkFocusInitial>
        {{ data.confirmText || 'Yes, send' }}
      </button>
    </div>
  `,
  styles: [`
    .header {
      padding: 20px 24px 10px;
    }
    .content {
      padding: 0 24px 10px !important;
    }
    .lead {
      font-size: 14px;
      line-height: 1.5;
      color: #4f5b6b;
      margin-bottom: 14px;
    }
    .source-card {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 12px;
      border: 1px solid #efcb7d;
      background: linear-gradient(180deg, #fff8dd 0%, #fff2c7 100%);
      border-radius: 10px;
      color: #6f5200;
      margin-bottom: 14px;
    }
    .corporate-info-card {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 12px;
      padding: 10px 12px;
      border-radius: 8px;
      border: 1px solid #e6ebf1;
      background: #f7f9fc;
    }
    .corporate-card-icon {
      width: 30px;
      height: 30px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      color: #fff;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    }
    .corporate-card-content {
      display: flex;
      flex-direction: column;
      min-width: 0;
      gap: 2px;
    }
    .corporate-card-label {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      opacity: 0.75;
    }
    .corporate-card-value {
      font-size: 13px;
      font-weight: 600;
      color: #2a3547;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .recipients-block {
      border: 1px solid #e6ebf1;
      border-radius: 10px;
      overflow: hidden;
    }
    .recipients-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: 600;
      padding: 10px 12px;
      background: #f7f9fc;
      border-bottom: 1px solid #e6ebf1;
    }
    .recipients-list {
      list-style: none;
      margin: 0;
      padding: 0;
      max-height: 220px;
      overflow: auto;
    }
    .recipients-list li {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      padding: 10px 12px;
      border-top: 1px solid #f0f3f8;
      font-size: 13px;
    }
    .recipient-name {
      font-weight: 600;
      color: #2a3547;
    }
    .recipient-email {
      color: #5d87ff;
      word-break: break-all;
      text-align: right;
    }
    .no-recipient {
      color: #c62828;
      font-size: 13px;
      padding: 12px;
      background: #fff5f5;
    }
    .actions {
      padding: 0 24px 24px !important;
      display: flex;
      gap: 10px;
      justify-content: flex-end;
    }
    .cancel-btn {
      background: #ff6692;
      color: #fff;
    }
    .confirm-btn {
      background: #5d87ff;
      color: #fff;
    }
  `]
})
export class ManualReminderConfirmationDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<ManualReminderConfirmationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ManualReminderConfirmationData
  ) {}
}

